import { useCallback, useState } from 'react';
import { useUserChoices } from '@/hooks/useUserChoices';
import type { NetWorthChangeDirection } from '@/interfaces/netWorthChanges';
import {
  NET_WORTH_CHANGES_RANGE_KEY,
  NET_WORTH_CHANGES_VIEW_KEY,
  type NetWorthChangeSelection,
  parseNetWorthChangeDirection,
  parseNetWorthChangeSelection,
  serializeNetWorthChangeSelection,
} from './netWorthChangeSelection';

export interface NetWorthChangesPreferences {
  selection: NetWorthChangeSelection;
  direction: NetWorthChangeDirection;
  setSelection: (selection: NetWorthChangeSelection) => void;
  setDirection: (direction: NetWorthChangeDirection) => void;
  /**
   * False until the saved choices have loaded. Asking for data before then
   * would fetch the default range, then fetch again once the saved one lands.
   */
  isReady: boolean;
}

/**
 * The widget's range and All / Gainers / Losers choice, kept in the advisor's
 * user settings so they survive a new session.
 *
 * A change made in this session wins over what was saved: the write is
 * optimistic, and the local value stops a slow round trip from snapping the
 * control back.
 */
export function useNetWorthChangesPreferences(): NetWorthChangesPreferences {
  const { getValue, setValue, isSettingsReady, isError } = useUserChoices();
  const [pickedSelection, setPickedSelection] =
    useState<NetWorthChangeSelection | null>(null);
  const [pickedDirection, setPickedDirection] =
    useState<NetWorthChangeDirection | null>(null);

  const selection =
    pickedSelection ??
    parseNetWorthChangeSelection(getValue(NET_WORTH_CHANGES_RANGE_KEY));
  const direction =
    pickedDirection ??
    parseNetWorthChangeDirection(getValue(NET_WORTH_CHANGES_VIEW_KEY));

  const setSelection = useCallback(
    (next: NetWorthChangeSelection) => {
      setPickedSelection(next);
      setValue(
        NET_WORTH_CHANGES_RANGE_KEY,
        serializeNetWorthChangeSelection(next),
      );
    },
    [setValue],
  );

  const setDirection = useCallback(
    (next: NetWorthChangeDirection) => {
      setPickedDirection(next);
      setValue(NET_WORTH_CHANGES_VIEW_KEY, next);
    },
    [setValue],
  );

  return {
    selection,
    direction,
    setSelection,
    setDirection,
    isReady: isSettingsReady || isError,
  };
}
